// src/pages/SupplierTeacherMonthlyReport.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { useLessons } from '../contexts/LessonsContext';
import SupplierMonthlyReportHeader from '../components/ui/SupplierMonthlyReportHeader';
import MonthPicker from '../components/ui/Monthpicker';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

/* ===================== Helpers ===================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'תאריך לא תקין';
  return d.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Same date/time parsing SartLesson.js uses.
const toLocalDate = (dateStr, timeStr = '00:00') => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh = 0, mm = 0] = String(timeStr).split(':').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

// Explicit terminal statuses (cancelled/no-show/completed) always
// win, same as SartLesson.js. But unlike SartLesson.js — whose own
// fallback for "time passed, still SCHEDULED/APPROVED" is AFTER ("the
// lesson ended"), since it's only deciding whether a join button can
// still work — this report applies a stricter rule for anything still
// SCHEDULED/APPROVED once its own end time has passed: nobody ever
// marked an actual outcome, so it's treated as NO_SHOW rather than a
// generic "ended", per explicit instruction for this report.
const getEffectivePhase = (lesson) => {
  const normalizedStatus = String(lesson?.status || '').toUpperCase().trim();
  const isCancelled = normalizedStatus === 'CANCELLED' || normalizedStatus === 'CANCELED' || normalizedStatus === 'CANCEL';
  const isNoShow = normalizedStatus === 'NO_SHOW' || normalizedStatus === 'MISSED' || normalizedStatus === 'NOT_HELD';
  const isCompleted = normalizedStatus === 'COMPLETED' || normalizedStatus === 'DONE';
  const isUnresolved = normalizedStatus === 'SCHEDULED' || normalizedStatus === 'APPROVED';

  if (isCancelled) return 'CANCELLED';
  if (isNoShow) return 'NO_SHOW';
  if (isCompleted) return 'AFTER';

  const startAt = toLocalDate(lesson?.date, lesson?.startTime);
  const endAt = toLocalDate(lesson?.date, lesson?.endTime);
  const now = new Date();

  if (!startAt) return 'UNKNOWN';
  if (now < startAt) return 'BEFORE';
  if (endAt && now > endAt) return isUnresolved ? 'NO_SHOW' : 'AFTER';
  return 'LIVE';
};

const PHASE_META = {
  CANCELLED: { label: 'השיעור התבטל',     cls: 'bg-rose-50 text-rose-700 border-rose-100' },
  NO_SHOW:   { label: 'השיעור לא התקיים', cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  BEFORE:    { label: 'מתוזמן',           cls: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
  LIVE:      { label: 'מתקיים כעת',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  AFTER:     { label: 'השיעור הסתיים',    cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  UNKNOWN:   { label: '—',                cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const TYPE_LABEL = { private: 'פרטי', group: 'קבוצתי' };

const StatusBadge = ({ lesson }) => {
  const phase = getEffectivePhase(lesson);
  const meta = PHASE_META[phase] || PHASE_META.UNKNOWN;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

// Same API_BASE resolution used elsewhere for this ad-hoc team-roster
// fetch (SearchContext.jsx/SupplierProgramsContext.jsx/MyCourses.js) —
// no shared teacher-directory context exists yet.
const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_BASE = ENV_BASE.replace(/\/+$/, '') || 'https://www.doroosonline.co.il';

async function fetchMyTeamTeachers() {
  const token = localStorage.getItem('jwt') ?? localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/supplier/team`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    mode: 'cors',
    credentials: 'omit',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || data?.message || 'שגיאה בטעינת צוות המורים');
  return Array.isArray(data?.items) ? data.items : [];
}

/* ===================== Page ===================== */
const SupplierTeacherMonthlyReport = () => {
  const { user, showAlert } = useUser();
  const { getSupplierTeacherAllLessonsByMonth } = useLessons();
  const [searchParams, setSearchParams] = useSearchParams();

  const now = useRef(new Date()).current;
  const currentMonthStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;

  const [month, setMonth] = useState(searchParams.get('month') || currentMonthStr);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teacherId, setTeacherId] = useState(searchParams.get('teacherId') || '');
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([]);

  const isSupplier = (user?.role || 'student') === 'supplier';

  // Team roster for the picker.
  useEffect(() => {
    if (!isSupplier) return;
    let cancelled = false;
    (async () => {
      setTeachersLoading(true);
      try {
        const rows = await fetchMyTeamTeachers();
        if (cancelled) return;
        setTeachers(rows);
      } catch (e) {
        if (cancelled) return;
        showAlert?.(e?.message || 'שגיאה בטעינת צוות המורים', 'error');
      } finally {
        if (!cancelled) setTeachersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSupplier, showAlert]);

  // Keep the URL in sync so refreshing/sharing the link preserves the
  // selected teacher and month.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (teacherId) next.set('teacherId', teacherId);
    else next.delete('teacherId');
    next.set('month', month);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, month]);

  useEffect(() => {
    if (!isSupplier || !teacherId) {
      setLessons([]);
      return;
    }
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const rows = await getSupplierTeacherAllLessonsByMonth(month, teacherId);
        if (cancelled) return;
        setLessons(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (cancelled) return;
        showAlert?.(e?.message || 'שגיאה בטעינת הדוח', 'error');
        setLessons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [month, teacherId, isSupplier, getSupplierTeacherAllLessonsByMonth, showAlert]);

  const selectedTeacher = useMemo(
    () => teachers.find((t) => String(t.id) === String(teacherId)) || null,
    [teachers, teacherId]
  );

  const totals = useMemo(() => {
    const total = lessons.length;
    let ended = 0, cancelled = 0, noShow = 0;
    lessons.forEach((l) => {
      const phase = getEffectivePhase(l);
      if (phase === 'AFTER') ended++;
      else if (phase === 'CANCELLED') cancelled++;
      else if (phase === 'NO_SHOW') noShow++;
    });
    return { total, ended, cancelled, noShow };
  }, [lessons]);

  const exportToExcel = () => {
    try {
      if (!lessons.length) {
        showAlert?.('אין נתונים לייצוא עבור החודש הנבחר', 'error');
        return;
      }
      const rows = lessons.map((l) => ({
        'מזהה שיעור':   l.id ?? '',
        'תאריך':         (l.date || '').toString(),
        'שעת התחלה':     (l.startTime || '').toString().slice(0, 5),
        'שעת סיום':      (l.endTime || '').toString().slice(0, 5),
        'נושא / כותרת':  l.topic || '',
        'קורס':          l.courseTitle || '',
        'סוג שיעור':     TYPE_LABEL[l.type] || l.type || '',
        'משתתפים':       Number(l.participantsCount || 0),
        'סטטוס':         PHASE_META[getEffectivePhase(l)]?.label || l.status || '',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [12, 14, 12, 12, 30, 24, 12, 12, 14].map((wch) => ({ wch }));
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: 8, r: rows.length - 1 } }) };
      wb.Workbook = wb.Workbook || {};
      wb.Workbook.Views = [{ RTL: true }];
      XLSX.utils.book_append_sheet(wb, ws, 'שיעורי מורה');
      const teacherLabel = selectedTeacher ? `${selectedTeacher.firstName}_${selectedTeacher.lastName}` : 'מורה';
      XLSX.writeFileXLSX(wb, `doroos_teacher_lessons_${teacherLabel}_${month}.xlsx`);
      showAlert?.('הדוח יוצא בהצלחה לקובץ Excel', 'success');
    } catch (e) {
      showAlert?.(e?.message || 'שגיאה בייצוא ל-Excel', 'error');
    }
  };

  if (!isSupplier) {
    return (
      <>
        <SupplierMonthlyReportHeader title="שיעורי מורה" subtitle="עמוד זה זמין למרכזי למידה בלבד" />
        <div className="min-h-screen bg-gray-100 max-w-8xl mx-auto p-4 sm:p-6" dir="rtl">
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין הרשאה</h3>
            <p className="text-gray-600">עמוד זה זמין למרכזי למידה בלבד.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="md:min-h-screen bg-gray-100">
      <SupplierMonthlyReportHeader
        title="שיעורי מורה"
        subtitle="בחרו מורה מהצוות כדי לצפות בכל השיעורים שלו/שלה בתקופה שנבחרה"
      />
      {(loading || teachersLoading) && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div className="mx-auto max-w-8xl px-3 sm:px-4 md:px-6 lg:px-8 pt-4 pb-8" dir="rtl">

        {/* ── Teacher picker + month picker ── */}
        <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="teacher-select" className="text-sm font-semibold text-gray-600 whitespace-nowrap">
              מורה:
            </label>
            <select
              id="teacher-select"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
              className="min-w-[220px] px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-800 outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            >
              <option value="">בחר מורה...</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
              ))}
            </select>
          </div>

          <MonthPicker value={month} onChange={(v) => setMonth(v)} maxDate={now} minYear={2023} locale="he" />
        </div>

        {!teacherId ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5.5 20a6.5 6.5 0 0 1 13 0" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">בחרו מורה כדי להציג את השיעורים</h3>
            <p className="text-gray-600 text-sm">יוצגו כל השיעורים של המורה שנבחר/ה בחודש שנבחר, בכל סטטוס</p>
          </div>
        ) : (
          <>
            {/* ── Status KPI cards ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 sm:p-4 rounded-xl border border-indigo-200">
                <p className="text-xs sm:text-sm text-indigo-700">סה״כ שיעורים</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-900">{totals.total}</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 sm:p-4 rounded-xl border border-green-200">
                <p className="text-xs sm:text-sm text-green-700">הסתיימו</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">{totals.ended}</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-red-50 p-3 sm:p-4 rounded-xl border border-rose-200">
                <p className="text-xs sm:text-sm text-rose-700">בוטלו</p>
                <p className="text-xl sm:text-2xl font-bold text-rose-900">{totals.cancelled}</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 rounded-xl border border-amber-200">
                <p className="text-xs sm:text-sm text-amber-700">לא התקיימו</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">{totals.noShow}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
              <p className="text-sm text-gray-500">
                {selectedTeacher ? `${selectedTeacher.firstName} ${selectedTeacher.lastName}` : ''}
              </p>
              <button
                onClick={exportToExcel}
                disabled={loading || lessons.length === 0}
                className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                ייצוא ל-Excel
              </button>
            </div>

            {lessons.length === 0 && !loading ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">אין שיעורים לחודש שנבחר</h3>
                <p className="text-gray-600 text-sm">נסה לבחור חודש אחר</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                        <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תאריך</th>
                        <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">שעה</th>
                        <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">נושא</th>
                        <th className="hidden md:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">קורס</th>
                        <th className="hidden sm:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">סוג</th>
                        <th
                          className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap"
                          title="לשיעור מתוזמן: מספר הנרשמים עד כה (עשוי עוד לגדול). לשיעור שבוצע: מספר הנרשמים הסופי."
                        >
                          משתתפים
                        </th>
                        <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">סטטוס</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {lessons.map((lesson, idx) => {
                        const date      = (lesson.date || '').toString();
                        const startTime = (lesson.startTime || '').toString().slice(0, 5);
                        const endTime   = (lesson.endTime || '').toString().slice(0, 5);
                        const isUpcoming = getEffectivePhase(lesson) === 'BEFORE';

                        return (
                          <tr key={lesson.id || `${date}-${startTime}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-3 sm:px-4 py-3 text-xs text-gray-800 whitespace-nowrap font-medium">{formatDate(date)}</td>
                            <td className="px-3 sm:px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{startTime}{endTime ? `–${endTime}` : ''}</td>
                            <td className="px-3 sm:px-4 py-3 text-xs text-gray-900">
                              <div className="max-w-[160px] sm:max-w-[220px] truncate font-medium" title={lesson.topic}>{lesson.topic || '—'}</div>
                            </td>
                            <td className="hidden md:table-cell px-3 sm:px-4 py-3 text-xs text-gray-500">
                              <div className="max-w-[180px] truncate" title={lesson.courseTitle}>{lesson.courseTitle || '—'}</div>
                            </td>
                            <td className="hidden sm:table-cell px-3 sm:px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                              {TYPE_LABEL[lesson.type] || lesson.type || '—'}
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <span
                                className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100"
                                title={isUpcoming ? 'נרשמו עד כה — המספר עשוי עוד לגדול עד מועד השיעור' : 'מספר הנרשמים הסופי לשיעור זה'}
                              >
                                {lesson.participantsCount ?? 0}
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <StatusBadge lesson={lesson} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupplierTeacherMonthlyReport;